/**
 * The ONE order-placement client: OpenMarkets' official order SDK (`@openmarketsai/order-sheet`).
 * Every buy, cancel, constraints read and preflight in greenbook goes through it; greenbook no
 * longer builds `/orders/buy` bodies itself.
 *
 * Wiring:
 *   - Credential: `{ auth }` = the SAME app-sdk instance omauth.ts owns (`getOmAuth()`). The
 *     SDK calls its `getApiKey()` per request, so the 7-day key renews silently and there's
 *     still only one refresher. Never a second `createOpenMarketsAuth`.
 *   - Stream OFF: greenbook's own socket (ws.ts) already holds the account's stream connection
 *     and subscribes `orders` + `balance`. The SDK would open a second one. With
 *     `stream.enabled: false` it polls `GET /auth/orders` for a resting/pending order instead,
 *     and {@link forwardStreamFrame} feeds greenbook's own frames into the client's event bus
 *     so open sheets still update the moment the push lands.
 *   - Base URL: the SDK wants the API ROOT (it adds `/flow/v1/...` itself), so
 *     `VITE_OPENMARKETS_BASE` is used as-is, minus any `/flow/v1` suffix.
 *
 * Money safety: `realMoney.enabled` is fixed when a client is created, so the client is keyed
 * on (identity, armed). Armed = greenbook's global mode is Real AND the session holds
 * `trade:execute`. Flip either one and the old client is destroyed and a new one built, so a
 * paper-mode client can never carry a real leg (`om.buy()` throws `real_money_disabled` and a
 * sheet blocks `real_money_disabled`). `realMoney.confirm` stays at the SDK default (true): a
 * second click per real order.
 *
 * Signed out, or on the legacy pasted key (no SDK session to hand over), there is no client.
 */
import { useSyncExternalStore } from "react";
import {
  createOrderSheetClient,
  type MoneyMode,
  type OrderSheetClient,
  type StreamBalanceUpdate,
  type StreamOrderSettled,
  type StreamOrderUpdate,
} from "@openmarketsai/order-sheet";
import { authIdentity, canTrade, getOmAuth, hasSdkSession, subscribeAuth } from "./omauth.js";
import { getTradingMode, tradingModeStore } from "./trading.js";

/** The API root for the order SDK (it appends `/flow/v1/...`). Undefined → the SDK's default. */
export function orderApiRoot(raw: string | undefined = import.meta.env.VITE_OPENMARKETS_BASE): string | undefined {
  const trimmed = raw?.trim().replace(/\/+$/, "");
  if (!trimmed) return undefined;
  return trimmed.replace(/\/flow\/v1$/, "");
}

/** May the current session place REAL orders right now? Global Real mode AND the trade scope. */
export function realMoneyArmed(): boolean {
  return getTradingMode() === "real" && canTrade();
}

/** The mode every sheet / leg is sent with. Paper unless armed. */
export function orderMode(): MoneyMode {
  return realMoneyArmed() ? "real" : "paper";
}

let current: { key: string; client: OrderSheetClient } | null = null;
let openSheets = 0;

/** What the client is keyed on; null = no client (signed out / no SDK session). */
function clientKey(): string | null {
  const id = authIdentity();
  if (!id || !hasSdkSession()) return null;
  return `${id}|${realMoneyArmed() ? "real" : "paper"}`;
}

/**
 * The client for the current credential and armed state, creating it on first use and
 * replacing it when either changes. Null when there is no SDK session.
 */
export function getOrderClient(): OrderSheetClient | null {
  const key = clientKey();
  if (current && current.key === key) return current.client;
  resetOrderClient();
  if (!key) return null;
  const armed = key.endsWith("|real");
  const client = createOrderSheetClient({
    auth: getOmAuth(),
    baseUrl: orderApiRoot(),
    // greenbook's ws.ts owns the one stream connection the plan allows; the SDK polls instead.
    stream: { enabled: false },
    realMoney: { enabled: armed, confirm: true },
    defaults: { mode: armed ? "real" : "paper" },
    labels: { practice: "Paper", real: "Real" },
    // The SDK's default is `{ set: setTimeout, clear: clearTimeout }`, called as `timers.set(…)`:
    // in a browser that's an unbound setTimeout → "Illegal invocation", which the sheet reports
    // as network_error on every load (see FRICTION.md). Wrap them so `this` never matters.
    timers: {
      set: ((fn: () => void, ms?: number) => globalThis.setTimeout(fn, ms)) as typeof setTimeout,
      clear: ((id?: ReturnType<typeof setTimeout>) => globalThis.clearTimeout(id)) as typeof clearTimeout,
    },
  });
  current = { key, client };
  return client;
}

/**
 * Session switch / sign-out: drop every cache the SDK holds and every sheet it opened. Called
 * by the session guard on any identity change, and whenever the armed state changes.
 */
export function resetOrderClient(): void {
  if (!current) return;
  const { client } = current;
  current = null;
  try {
    client.destroy(); // = reset() (caches, sheets, stream) + drop event handlers
  } catch {
    /* ignore */
  }
}

// The client only ever changes when the credential or the trading mode does, and both of
// those stores already notify — so subscribe to them rather than keeping a third emitter.
function subscribe(cb: () => void): () => void {
  const offAuth = subscribeAuth(cb);
  const offMode = tradingModeStore.subscribe(cb);
  return () => {
    offAuth();
    offMode();
  };
}

/** React: the order client (re-renders when the credential or the armed state changes). */
export function useOrderClient(): OrderSheetClient | null {
  return useSyncExternalStore(subscribe, getOrderClient, () => null);
}

/** A ticket is open: balance pushes should actively re-read the constraints. */
export function trackOpenSheet(): () => void {
  openSheets++;
  let done = false;
  return () => {
    if (done) return;
    done = true;
    openSheets = Math.max(0, openSheets - 1);
  };
}

/**
 * Feed a frame from greenbook's own stream (ws.ts) into the order client, so the SDK's sheets
 * follow fills / settlement / balances without a second socket. Only the private channels the
 * SDK reads are forwarded. A no-op without a client.
 */
export function forwardStreamFrame(msg: { type?: unknown; [k: string]: unknown }): void {
  const client = current?.client;
  if (!client) return;
  switch (msg.type) {
    case "order.update":
      client.events.emit("order.update", msg as unknown as StreamOrderUpdate);
      return;
    case "order.settled":
      client.events.emit("order.settled", msg as unknown as StreamOrderSettled);
      return;
    case "balance.update":
      client.events.emit("balance.update", msg as unknown as StreamBalanceUpdate);
      // Balances feed the sheet's local checks. Mark the cached constraints stale and, with a
      // ticket open, re-read them now (the SDK single-flights the request).
      client.invalidateConstraints();
      if (openSheets > 0) client.constraints({ force: true }).catch(() => {});
      return;
    case "connected":
      // Our socket (re)connected: changes-only channels have no replay, so sheets re-read
      // their tracked orders.
      client.events.emit("stream.connected", {
        server_time: typeof msg.server_time === "string" ? msg.server_time : null,
      });
      return;
  }
}

/** Tests only. */
export function __resetOrderClientForTests(): void {
  resetOrderClient();
  openSheets = 0;
}
